import * as React from 'react';

import { CrashPage } from 'unit-error-pages/pages';
import { CommonApi } from '../../api/server';
import { initializeTagManager } from '../../units/analytics/gtm/gtm';
import { EnvironmentConfiguration } from './environment-configuration-props';
import { ExceptionHandlingUtils } from './exception-handling-utils';

export const EnvironmentContext = React.createContext<EnvironmentConfiguration>(null);

interface State {
  environmentConfiguration: EnvironmentConfiguration;
  isDevelopment: boolean;
  isError: boolean;
}

const developmentEnvironmentsNames = ['Development', 'Integration'];

export class EnvironmentContextProvider extends React.PureComponent<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      environmentConfiguration: null,
      isDevelopment: false,
      isError: false,
    };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(error, errorInfo);
    if (this.state.environmentConfiguration && !this.state.isDevelopment) {
      ExceptionHandlingUtils.captureException(error, errorInfo);
    }
    this.setState({ isError: true });
  }

  public componentDidMount(): void {
    this.fetchEnvironmentConfiguration();
  }

  public componentDidUpdate(_props: {}, prevState: State): void {
    const { environmentConfiguration, isDevelopment } = this.state;
    if (prevState.environmentConfiguration === null && environmentConfiguration !== null) {
      const { environmentName, sentryDsn, release, chargebeeSiteName, gtmKey } = environmentConfiguration;
      if (!isDevelopment) {
        ExceptionHandlingUtils.initSentryAndOverrideConsoleError(
          sentryDsn,
          release,
          environmentName,
        );
      }
      if (chargebeeSiteName) {
        Chargebee.init({ site: chargebeeSiteName });
      }

      if (gtmKey) {
        initializeTagManager(gtmKey);
      }
    }
  }

  public render(): React.ReactNode {
    return (
      <EnvironmentContext.Provider value={this.state.environmentConfiguration}>
        {this.state.isError ? <CrashPage/> : this.props.children}
      </EnvironmentContext.Provider>
    );
  }

  private fetchEnvironmentConfiguration(): void {
    CommonApi.get<EnvironmentConfiguration>(
      '/common/environment-configuration',
      'Error while fetching environment configuration',
    ).then(environmentConfiguration => {
      const isDevelopment = developmentEnvironmentsNames.includes(environmentConfiguration.environmentName) ||
        process.env.NODE_ENV === 'development';
      this.setState({ environmentConfiguration, isDevelopment });
    });
  }
}
